import { CheckCircleFilled } from '@ant-design/icons';
import Image from 'next/image';
import Link from 'next/link';
import styles from './AuthBrandRail.module.scss';

/**
 * AuthBrandRail - Brand presence panel for auth pages (RIGHT SIDE)
 * Full-height blue panel with animated dashboard screenshot
 *
 * @param {Object} props
 * @param {string} props.headline - Main headline (AI-powered messaging)
 * @param {string} props.subheadline - Supporting subheadline (contextual)
 * @param {Array<string>} props.features - Array of 3-4 feature strings with checkmarks
 * @param {string} props.variant - Animation variant (1-4) for unique blob patterns per page
 */
export const AuthBrandRail = ({ headline, subheadline, features = [], variant = '1' }) => {
  // Blob configurations for each variant (positions, sizes, opacity)
  const blobConfigs = {
    1: {
      blob1: { cx: 150, cy: 150, r: 400, opacity: 0.25 },
      blob2: { cx: 850, cy: 300, rx: 300, ry: 150, opacity: 0.4 },
      blob3: { x: 0, y: 700, width: 600, height: 250, opacity: 0.3 },
      blob4: { cx: 500, cy: 900, r: 100, opacity: 0.2 },
    },
    2: {
      blob1: { cx: 800, cy: 200, r: 350, opacity: 0.28 },
      blob2: { cx: 200, cy: 600, rx: 250, ry: 180, opacity: 0.35 },
      blob3: { x: 400, y: 50, width: 550, height: 280, opacity: 0.32 },
      blob4: { cx: 100, cy: 850, r: 120, opacity: 0.22 },
    },
    3: {
      blob1: { cx: 500, cy: 100, r: 380, opacity: 0.26 },
      blob2: { cx: 900, cy: 700, rx: 280, ry: 160, opacity: 0.38 },
      blob3: { x: 100, y: 400, width: 580, height: 240, opacity: 0.29 },
      blob4: { cx: 700, cy: 200, r: 110, opacity: 0.24 },
    },
    4: {
      blob1: { cx: 300, cy: 800, r: 420, opacity: 0.24 },
      blob2: { cx: 100, cy: 200, rx: 320, ry: 140, opacity: 0.42 },
      blob3: { x: 200, y: 600, width: 620, height: 260, opacity: 0.31 },
      blob4: { cx: 850, cy: 450, r: 95, opacity: 0.21 },
    },
  };

  const config = blobConfigs[variant] || blobConfigs['1'];
  return (
    <div className={styles.brandRail}>
      {/* SVG Blob Background Layer - Enhanced Tech Wave Effect */}
      <svg
        className={styles.abstractBackgroundSvg}
        viewBox="0 0 1000 1000"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <defs>
          <filter id="blur-filter">
            <feGaussianBlur in="SourceGraphic" stdDeviation="200" />
          </filter>
        </defs>

        {/* Blob 1: Slowest, longest path - Background wash */}
        <circle
          cx={config.blob1.cx}
          cy={config.blob1.cy}
          r={config.blob1.r}
          fill={`rgba(255, 255, 255, ${config.blob1.opacity})`}
          filter="url(#blur-filter)"
          className={styles.blobSlow}
        />

        {/* Blob 2: Medium-reverse - Complexity layer */}
        <ellipse
          cx={config.blob2.cx}
          cy={config.blob2.cy}
          rx={config.blob2.rx}
          ry={config.blob2.ry}
          fill={`rgba(100, 150, 255, ${config.blob2.opacity})`}
          filter="url(#blur-filter)"
          className={styles.blobMediumReverse}
        />

        {/* Blob 3: Fast rectangular sweep - Dynamic movement */}
        <rect
          x={config.blob3.x}
          y={config.blob3.y}
          width={config.blob3.width}
          height={config.blob3.height}
          fill={`rgba(255, 255, 255, ${config.blob3.opacity})`}
          filter="url(#blur-filter)"
          className={styles.blobFast}
        />

        {/* Blob 4: Longest cycle - Subtle accent */}
        <circle
          cx={config.blob4.cx}
          cy={config.blob4.cy}
          r={config.blob4.r}
          fill={`rgba(60, 100, 255, ${config.blob4.opacity})`}
          filter="url(#blur-filter)"
          className={styles.blobLongest}
        />
      </svg>

      <div className={styles.brandContent}>
        {/* WHITE Logo at Top - 33% Larger */}
        <Link href="/" className={styles.logoLink}>
          <Image
            src="/images/bm-logo-white.svg"
            alt="Brandymail - Intelligent Sending Domain Management"
            width={240}
            height={60}
            priority
          />
        </Link>

        {/* Headline Section */}
        <div className={styles.headlineSection}>
          <h2 className={styles.headline}>{headline}</h2>
          {subheadline && <p className={styles.subheadline}>{subheadline}</p>}
        </div>

        {/* Features List */}
        {features && features.length > 0 && (
          <div className={styles.featuresSection}>
            {features.map((feature, index) => (
              <div key={index} className={styles.feature}>
                <CheckCircleFilled className={styles.featureIcon} />
                <span className={styles.featureText}>{feature}</span>
              </div>
            ))}
          </div>
        )}

        {/* Dashboard Screenshots - Desktop Only - Sequential Fade & Slide */}
        <div className={styles.screenshotContainer}>
          {/* Main Dashboard Image - Fades in first */}
          <div className={styles.dashboardImageWrapper}>
            <Image
              src="/images/bm-auth-image-dashboard.png"
              alt="Brandymail Dashboard - Email Security Analytics"
              width={600}
              height={450}
              className={styles.dashboardImage}
              priority
            />
          </div>

          {/* AI Overlay Image - Fades in second, overlays on right */}
          <div className={styles.aiImageWrapper}>
            <Image
              src="/images/bm-auth-image-brandy-ai.png"
              alt="Brandy AI - Intelligent Email Analysis"
              width={280}
              height={320}
              className={styles.aiImage}
              priority
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthBrandRail;
